// ═══════════════════════════════════════════════════════════════
// DonorDetailView — 2-Column Main Area (Tabbed)
// Path: src/components/donorDetail/DonorDetailMain.jsx
//
// v5.1.0 — Added Contact Reports tab (Phase 3 of donor contact reports)
// v5.0.0 — Tabbed layout replaces single scroll:
//   Tab 1: Cultivation     (Notes, Cultivation Intelligence, Comms)
//   Tab 2: Giving          (Gifts, Campaigns, Show Sponsorships)
//   Tab 3: Contact Reports (call report history + new report form)
//   Tab 4: Profile         (Personal, Professional, Donor Details, Address, Custom)
//
// Giving tab hidden when canViewGifts === false.
// Contact Reports tab hidden when canFileContactReports === false.
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { useApp } from "../../context/AppContext";
import {
  User,
  Mail,
  Phone,
  CreditCard,
  MapPin,
  Briefcase,
  FileText,
  MessageSquare,
  Send,
  Layers,
  Sprout,
  Gift,
  Film,
  Megaphone,
  Heart,
  UserCircle,
  Notebook,
  Target,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { MEMBER_TYPES, CFRE_OPTIONS } from "../../constants/memberTypes";
import { FormInput } from "../ui/FormControls";
import { TabBar } from "../ui/TabBar";
import { GiftsSection } from "../features/GiftsSection";
import { MemberSponsorshipsSection } from "../shows/MemberSponsorshipsSection";
import {
  Field,
  SectionHead,
  AccentSection,
  OptOutBadge,
} from "./DonorDetailShared";
import { CultivationContent } from "./CultivationSection";
import { CommunicationContent } from "./CommunicationSection";
import { CampaignsSection } from "./CampaignsSection";
import { CustomFieldsContent } from "./CustomFieldsSection";
import { AttachmentList } from "./AttachmentList";
import { ContactReportsSection } from "./ContactReportsSection";
import { CapacitySection } from "./CapacitySection";

// ── Tab Definitions ──────────────────────────────────────────
// Built dynamically to honor both canViewGifts and canFileContactReports.
// Tab order is: Profile → Cultivation → Giving → Contact Reports.

const buildDetailTabs = ({ canViewGifts, canFileContactReports }) => {
  const tabs = [
    { id: "profile", label: "Profile", icon: UserCircle },
    { id: "cultivation", label: "Cultivation", icon: Sprout },
    { id: "capacity", label: "Capacity", icon: Target },
  ];
  if (canViewGifts) {
    tabs.push({ id: "giving", label: "Giving", icon: Heart });
  }
  if (canFileContactReports) {
    tabs.push({ id: "contactReports", label: "Contact Reports", icon: Notebook });
  }
  return tabs;
};

// ═══════════════════════════════════════════════════════════════
// DonorDetailMain
// ═══════════════════════════════════════════════════════════════

export const DonorDetailMain = ({
  // Data
  detailForm,
  selectedMember,
  palette,
  effective,
  // Cultivation data
  engagement,
  engPathway,
  scoreColor,
  daysColor,
  scorePct,
  selectedPersona,
  suggestion,
  // Permissions
  canEdit,
  canLogContact,
  canViewGifts,
  canFileContactReports,
  // Handlers
  updateField,
  updateMember,
  handleGiftSaved,
  // Modal triggers
  onLogContact,
  onEditLog,
  onDeleteLog,
  // Custom fields
  customFields,
  // Section collapse state
  collapsed2Col,
  toggle2Col,
  // Section editing
  editingSection,
  startSectionEdit,
  cancelSectionEdit,
  saveSectionEdit,
  sectionFieldProps,
  // Messaging
  showMessage,
}) => {
  const { navContext } = useApp();
  // Default to Profile, but if the user arrived from the Capacity
  // triage view land on the Capacity tab — they're in assess mode.
  const [activeTab, setActiveTab] = useState(
    navContext?.from === "capacity" ? "capacity" : "profile"
  );
  const tabs = buildDetailTabs({ canViewGifts, canFileContactReports });

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* ── Tab Bar ──────────────────────────────────────────── */}
      <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* ── Tab Content (scrollable) ────────────────────────── */}
      <div style={{ flex: 1, padding: "20px 28px", overflowY: "auto" }}>

        {/* ════════════════════════════════════════════════════ */}
        {/* TAB: Cultivation                                    */}
        {/* ════════════════════════════════════════════════════ */}
        {activeTab === "cultivation" && (
          <>
            {/* ── Notes & Intelligence ──────────────────────── */}
            <SectionHead
              title="Notes & Intelligence"
              icon={FileText}
              palette={palette}
              accentColor={palette.accent1}
            />
            <textarea
              value={detailForm.notes || ""}
              onChange={(e) => {
                if (canEdit) updateField("notes", e.target.value);
              }}
              onBlur={() => {
                if (canEdit)
                  updateMember(selectedMember.id, { notes: detailForm.notes }, true);
              }}
              rows={3}
              readOnly={!canEdit}
              placeholder={
                canEdit
                  ? "What makes this member tick? Interests, motivations, engagement preferences..."
                  : ""
              }
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 8,
                fontSize: FONT_SIZES.md,
                fontFamily: FONT_FAMILY,
                outline: "none",
                background: canEdit ? palette.inputBg : `${palette.inputBg}80`,
                color: palette.inputColor,
                border: `1px solid ${palette.inputBorder}`,
                resize: canEdit ? "vertical" : "none",
                marginBottom: 10,
                boxSizing: "border-box",
              }}
            />
            <AttachmentList
              memberId={selectedMember?.id}
              attachments={detailForm.attachments || []}
              canEdit={canEdit}
              palette={palette}
              onAttachmentsChange={(updated) => {
                updateField("attachments", updated);
                updateMember(selectedMember.id, { attachments: updated }, true);
              }}
              showMessage={showMessage}
            />

            {/* ── Cultivation Intelligence ──────────────────── */}
            <SectionHead
              title="Cultivation Intelligence"
              icon={Sprout}
              palette={palette}
              accentColor={palette.accent1}
            />
            <div style={{ marginBottom: 20 }}>
              <CultivationContent
                engagement={engagement}
                engPathway={engPathway}
                scoreColor={scoreColor}
                daysColor={daysColor}
                scorePct={scorePct}
                selectedPersona={selectedPersona}
                suggestion={suggestion}
                detailForm={detailForm}
                canEdit={canEdit}
                updateField={updateField}
                updateMember={updateMember}
                selectedMember={selectedMember}
                palette={palette}
              />
            </div>

            {/* ── Communication History ─────────────────────── */}
            <SectionHead
              title="Communication History"
              icon={MessageSquare}
              palette={palette}
              accentColor={palette.accent1}
            />
            <div style={{ marginBottom: 20 }}>
              <CommunicationContent
                contactLog={detailForm.contactLog || []}
                canLogContact={canLogContact}
                onLogContact={onLogContact}
                onEditLog={onEditLog}
                onDeleteLog={onDeleteLog}
                palette={palette}
              />
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════ */}
        {/* TAB: Capacity                                       */}
        {/* ════════════════════════════════════════════════════ */}
        {activeTab === "capacity" && (
          <CapacitySection
            onLogContact={onLogContact}
            engagementScore={engagement?.score}
          />
        )}

        {/* ════════════════════════════════════════════════════ */}
        {/* TAB: Giving                                         */}
        {/* ════════════════════════════════════════════════════ */}
        {activeTab === "giving" && canViewGifts && (
          <>
            {/* ── Gift History ──────────────────────────────── */}
            <AccentSection
              title={
                <>
                  Gift History
                  {(selectedMember?.spektrixId || selectedMember?.cpDonorId) && (
                    <span style={{ fontSize: "0.7rem", fontWeight: 500, opacity: 0.85, marginLeft: 10 }}>
                      {selectedMember.spektrixId
                        ? `Spektrix ID: ${selectedMember.spektrixId}`
                        : `CP Donor ID: ${selectedMember.cpDonorId}`}
                    </span>
                  )}
                </>
              }
              icon={Gift}
              color={effective.color}
            >
              <GiftsSection
                member={selectedMember}
                palette={palette}
                canEdit={canEdit}
                onGiftSaved={handleGiftSaved}
              />
            </AccentSection>

            {/* ── Campaigns ─────────────────────────────────── */}
            <AccentSection title="Campaigns" icon={Megaphone} color={effective.color}>
              <CampaignsSection
                member={selectedMember}
                palette={palette}
              />
            </AccentSection>

            {/* ── Show Sponsorships ─────────────────────────── */}
            <AccentSection title="Show Sponsorships" icon={Film} color={effective.color}>
              <MemberSponsorshipsSection
                member={selectedMember}
                palette={palette}
                canEdit={canEdit}
                canViewGifts={canViewGifts}
              />
            </AccentSection>
          </>
        )}

        {/* ════════════════════════════════════════════════════ */}
        {/* TAB: Contact Reports                                */}
        {/* ════════════════════════════════════════════════════ */}
        {activeTab === "contactReports" && canFileContactReports && (
          <ContactReportsSection
            member={selectedMember}
            palette={palette}
            canEdit={canEdit}
          />
        )}

        {/* ════════════════════════════════════════════════════ */}
        {/* TAB: Profile                                        */}
        {/* ════════════════════════════════════════════════════ */}
        {activeTab === "profile" && (
          <>
            {/* ── Custom Fields ────────────────────────────── */}
            {customFields.length > 0 && (
              <>
                <SectionHead
                  title="Custom Fields"
                  icon={Layers}
                  palette={palette}
                  accentColor={palette.accent1}
                  collapsible
                  collapsed={collapsed2Col.custom}
                  onToggle={() => toggle2Col("custom")}
                />
                {!collapsed2Col.custom && (
                  <div style={{ marginBottom: 20 }}>
                    <CustomFieldsContent
                      customFields={customFields}
                      detailForm={detailForm}
                      updateField={updateField}
                      updateMember={updateMember}
                      selectedMember={selectedMember}
                      palette={palette}
                    />
                  </div>
                )}
              </>
            )}

            {/* ── Personal Information ─────────────────────── */}
            <SectionHead
              title="Personal Information"
              icon={User}
              palette={palette}
              accentColor={palette.accent1}
              collapsible
              collapsed={collapsed2Col.personal}
              onToggle={() => toggle2Col("personal")}
              canEdit={canEdit}
              editingThis={editingSection === "personal"}
              onEdit={() => {
                if (collapsed2Col.personal) toggle2Col("personal");
                startSectionEdit("personal");
              }}
              onSave={() =>
                saveSectionEdit("personal", [
                  "firstName",
                  "lastName",
                  "credentialedFullName",
                  "title",
                  "email",
                  "phone",
                  "birthdate",
                  "website",
                  "organization",
                ])
              }
              onCancel={cancelSectionEdit}
            />
            {!collapsed2Col.personal && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  marginBottom: 20,
                  padding: editingSection === "personal" ? "16px" : 0,
                  background: editingSection === "personal" ? "#ffffff" : "transparent",
                  border:
                    editingSection === "personal"
                      ? `1px solid ${palette.cardBorder}`
                      : "none",
                  borderTop: "none",
                  borderRadius: editingSection === "personal" ? "0 0 8px 8px" : 0,
                }}
              >
                {(detailForm.donorType || "individual") === "organization" ? (
                  <>
                    <Field
                      label="Organization Name"
                      field="organization"
                      {...sectionFieldProps("personal")}
                    />
                    <Field label="Title" field="title" {...sectionFieldProps("personal")} />
                    <Field
                      label="Contact First Name"
                      field="firstName"
                      {...sectionFieldProps("personal")}
                    />
                    <Field
                      label="Contact Last Name"
                      field="lastName"
                      {...sectionFieldProps("personal")}
                    />
                  </>
                ) : (
                  <>
                    <Field
                      label="First Name"
                      field="firstName"
                      {...sectionFieldProps("personal")}
                    />
                    <Field
                      label="Last Name"
                      field="lastName"
                      {...sectionFieldProps("personal")}
                    />
                    <Field
                      label="Credentialed Name"
                      field="credentialedFullName"
                      {...sectionFieldProps("personal")}
                    />
                    <Field label="Title" field="title" {...sectionFieldProps("personal")} />
                  </>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: 600,
                      color: palette.text2,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Email{detailForm.noEmail && <OptOutBadge label="No Email" />}
                  </span>
                  {editingSection === "personal" ? (
                    <FormInput
                      value={detailForm.email || ""}
                      onChange={(v) => updateField("email", v)}
                      type="email"
                    />
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          fontSize: FONT_SIZES.md,
                          color: detailForm.email ? palette.text1 : palette.text3,
                        }}
                      >
                        {detailForm.email || "\u2014"}
                      </span>
                      {detailForm.email && (
                        <button
                          onClick={onLogContact}
                          title="Send email & log contact"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "4px 10px",
                            borderRadius: 6,
                            border: "1px solid #3b82f6",
                            background: "#eff6ff",
                            color: "#3b82f6",
                            fontSize: FONT_SIZES.xs,
                            fontWeight: 600,
                            fontFamily: FONT_FAMILY,
                            cursor: "pointer",
                          }}
                        >
                          <Send size={ICON_SIZES.xs} /> Email
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: 600,
                      color: palette.text2,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Phone{detailForm.noPhone && <OptOutBadge label="No Phone" />}
                    {detailForm.noText && <OptOutBadge label="No Text" />}
                  </span>
                  {editingSection === "personal" ? (
                    <FormInput
                      value={detailForm.phone || ""}
                      onChange={(v) => updateField("phone", v)}
                    />
                  ) : (
                    <span
                      style={{
                        fontSize: FONT_SIZES.md,
                        color: detailForm.phone ? palette.text1 : palette.text3,
                      }}
                    >
                      {detailForm.phone || "\u2014"}
                    </span>
                  )}
                </div>
                <Field
                  label="Birthdate"
                  field="birthdate"
                  {...sectionFieldProps("personal")}
                />
                <Field
                  label="Website"
                  field="website"
                  {...sectionFieldProps("personal")}
                />
              </div>
            )}

            {/* ── Professional ─────────────────────────────── */}
            <SectionHead
              title="Professional"
              icon={Briefcase}
              palette={palette}
              accentColor={palette.accent1}
              collapsible
              collapsed={collapsed2Col.professional}
              onToggle={() => toggle2Col("professional")}
              canEdit={canEdit}
              editingThis={editingSection === "professional"}
              onEdit={() => {
                if (collapsed2Col.professional) toggle2Col("professional");
                startSectionEdit("professional");
              }}
              onSave={() =>
                saveSectionEdit("professional", [
                  "organization",
                  "yearsExperience",
                  "designation",
                ])
              }
              onCancel={cancelSectionEdit}
            />
            {!collapsed2Col.professional && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  marginBottom: 20,
                  padding: editingSection === "professional" ? "16px" : 0,
                  background:
                    editingSection === "professional" ? "#ffffff" : "transparent",
                  border:
                    editingSection === "professional"
                      ? `1px solid ${palette.cardBorder}`
                      : "none",
                  borderTop: "none",
                  borderRadius: editingSection === "professional" ? "0 0 8px 8px" : 0,
                }}
              >
                <Field
                  label="Organization"
                  field="organization"
                  {...sectionFieldProps("professional")}
                />
                <Field
                  label="Years of Experience"
                  field="yearsExperience"
                  {...sectionFieldProps("professional")}
                />
                <Field
                  label="Designation"
                  field="designation"
                  {...sectionFieldProps("professional")}
                />
              </div>
            )}

            {/* ── Donor Details ────────────────────────────── */}
            <SectionHead
              title="Donor Details"
              icon={CreditCard}
              palette={palette}
              accentColor={palette.accent1}
              collapsible
              collapsed={collapsed2Col.membership}
              onToggle={() => toggle2Col("membership")}
              canEdit={canEdit}
              editingThis={editingSection === "membership"}
              onEdit={() => {
                if (collapsed2Col.membership) toggle2Col("membership");
                startSectionEdit("membership");
              }}
              onSave={() =>
                saveSectionEdit("membership", [
                  "spektrixId",
                  "cpDonorId",
                  "memberSince",
                  "startDate",
                  "endDate",
                ])
              }
              onCancel={cancelSectionEdit}
            />
            {!collapsed2Col.membership && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 16,
                  marginBottom: 20,
                  padding: editingSection === "membership" ? "16px" : 0,
                  background:
                    editingSection === "membership" ? "#ffffff" : "transparent",
                  border:
                    editingSection === "membership"
                      ? `1px solid ${palette.cardBorder}`
                      : "none",
                  borderTop: "none",
                  borderRadius: editingSection === "membership" ? "0 0 8px 8px" : 0,
                }}
              >
                <Field
                  label="Spektrix ID"
                  field="spektrixId"
                  {...sectionFieldProps("membership")}
                />
                <Field
                  label="CP Donor ID"
                  field="cpDonorId"
                  {...sectionFieldProps("membership")}
                />
                <Field
                  label="Donor Since"
                  field="memberSince"
                  {...sectionFieldProps("membership")}
                />
                <Field
                  label="Start Date"
                  field="startDate"
                  {...sectionFieldProps("membership")}
                />
                <Field
                  label="End Date"
                  field="endDate"
                  {...sectionFieldProps("membership")}
                />
                <Field
                  label="Org Pays Dues"
                  field="orgPaysDues"
                  options={["Yes", "No"]}
                  {...sectionFieldProps("membership")}
                />
                <Field
                  label="Auto-Renews"
                  field="autoRenews"
                  options={["Yes", "No"]}
                  {...sectionFieldProps("membership")}
                />
                <Field
                  label="Complimentary Reason"
                  field="complimentaryReason"
                  {...sectionFieldProps("membership")}
                />
              </div>
            )}

            {/* ── Address ──────────────────────────────────── */}
            <SectionHead
              title="Address"
              icon={MapPin}
              palette={palette}
              accentColor={palette.accent1}
              collapsible
              collapsed={collapsed2Col.address}
              onToggle={() => toggle2Col("address")}
              canEdit={canEdit}
              editingThis={editingSection === "address"}
              onEdit={() => {
                if (collapsed2Col.address) toggle2Col("address");
                startSectionEdit("address");
              }}
              onSave={() =>
                saveSectionEdit("address", [
                  "homeStreet",
                  "homeCity",
                  "homeState",
                  "homeZip",
                  "homeCountry",
                ])
              }
              onCancel={cancelSectionEdit}
            />
            {!collapsed2Col.address && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  marginBottom: 20,
                  padding: editingSection === "address" ? "16px" : 0,
                  background: editingSection === "address" ? "#ffffff" : "transparent",
                  border:
                    editingSection === "address"
                      ? `1px solid ${palette.cardBorder}`
                      : "none",
                  borderTop: "none",
                  borderRadius: editingSection === "address" ? "0 0 8px 8px" : 0,
                }}
              >
                <Field
                  label="Street"
                  field="homeStreet"
                  {...sectionFieldProps("address")}
                />
                <Field
                  label="City"
                  field="homeCity"
                  {...sectionFieldProps("address")}
                />
                <Field
                  label="State/Province"
                  field="homeState"
                  {...sectionFieldProps("address")}
                />
                <Field
                  label="Zip/Postal Code"
                  field="homeZip"
                  {...sectionFieldProps("address")}
                />
                <Field
                  label="Country"
                  field="homeCountry"
                  {...sectionFieldProps("address")}
                />
              </div>
            )}

            <div style={{ height: 40 }} />
          </>
        )}
      </div>
    </div>
  );
};
